import {
  transactionQuestionRowsShown,
  type PortalRequestResponse,
} from "@ledgerbox/domain"

/* What a client sees on a question about transactions. Pure, so each state
   is a test; transaction-question.tsx only draws it. */

type RequestItem = PortalRequestResponse["items"][number]
type Choice = { id: string; name: string }

export type QuestionView =
  /** The firm accepted it, or nothing is left to ask. */
  | { kind: "done"; text: string }
  /** The client answered. They can change it until the firm decides. */
  | { kind: "answered"; text: string }
  /** Still to answer. `suggestion` is the likely category, when there is one. */
  | { kind: "ask"; prompt: string; suggestion: Choice | null }

export const questionView = (input: {
  item: RequestItem
  categoryChoices: readonly Choice[]
  firmName: string
}): QuestionView => {
  const { item } = input
  const question = item.question
  if (item.status === "accepted") {
    return { kind: "done", text: `${input.firmName} took care of this` }
  }
  const names = new Map(
    input.categoryChoices.map((choice) => [choice.id, choice])
  )
  const answer = item.answer
  if (answer && item.status === "received") {
    const picked = answer.categoryId ? names.get(answer.categoryId) : undefined
    const note = typeof answer.value === "string" ? answer.value.trim() : ""
    return {
      kind: "answered",
      text: picked
        ? `You said ${picked.name}`
        : note
          ? `You said “${note}”`
          : "You answered",
    }
  }
  const single = (question?.rows.length ?? 0) === 1
  return {
    kind: "ask",
    prompt: single ? "What was this for?" : "What were these for?",
    suggestion: question?.suggestedCategoryId
      ? (names.get(question.suggestedCategoryId) ?? null)
      : null,
  }
}

/** The note field's label: "Or tell Harbor Books what these were". */
export const questionNoteLabel = (item: RequestItem, firmName: string) =>
  (item.question?.rows.length ?? 0) === 1
    ? `Or tell ${firmName} what this was`
    : `Or tell ${firmName} what these were`

/** The rows under a question: ten, then "Show all 14". */
export const visibleQuestionRows = <Row>(
  rows: readonly Row[],
  showAll: boolean
): { rows: readonly Row[]; more: string | null } =>
  showAll || rows.length <= transactionQuestionRowsShown
    ? { rows, more: null }
    : {
        rows: rows.slice(0, transactionQuestionRowsShown),
        more: `Show all ${rows.length}`,
      }

/** An amount as the client reads it, in their statement's currency: money
    in "+$11,400.00", money out "−$186.40", as the firm's review page shows
    it, so a deposit and a charge do not look the same. */
export const questionAmount = (
  amount: string | null,
  currency: string | null
): string => {
  const value = Number(amount)
  if (amount === null || !Number.isFinite(value)) return ""
  const code = currency && /^[A-Za-z]{3}$/.test(currency) ? currency : "USD"
  const money = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: code.toUpperCase(),
  }).format(Math.abs(value))
  return value < 0 ? `\u2212${money}` : value > 0 ? `+${money}` : money
}
